import { Injectable } from "@angular/core";
import { RudderStackService } from "./rudderstack.service";
import {
  detectDeviceType,
  detectOperatingSystem,
  getUserAgent,
} from "./analytics-device";


/**
 * The single seam for analytics in this app.
 *
 * RudderStack is the ONLY destination. The Segment path this replaced ran
 * through globals defined by the remotely hosted km_event_fire.js -
 * push_event_properties (POSTed to an AWS Lambda that wrote to Segment
 * server-side), firePageLoadEvent, and a bare window.analytics.page() - plus
 * direct lambda POSTs from seven components. All of that is gone.
 *
 * ---------------------------------------------------------------------------
 * `eg-timestamp` IS STAMPED HERE NOW.
 * ---------------------------------------------------------------------------
 * createSEGEventsArray (km_event_fire.js:2661) used to do:
 *
 *     computed_properties["eg-timestamp"] = new Date().toISOString();
 *
 * mutating the caller's object in place. Downstream consumers still expect that
 * field, and with the CDN script no longer in the path nothing else supplies it,
 * so every method below stamps it before handing the event over.
 *
 * The device and keyword property builders at the bottom of this file are
 * verbatim ports of the CDN's get_device / add_keyword_properties for the same
 * reason: those properties used to be injected by the script and would
 * otherwise vanish from the payload.
 */
@Injectable()
export class EventService {
  /**
   * Query params captured once at bootstrap. The CDN computes its `url_vars` at
   * script load and never refreshes it, so freezing here is what replicates its
   * behaviour in an SPA where the URL changes under us.
   */
  private readonly urlVars: { [key: string]: string };

  constructor(private rudderStackService: RudderStackService) {
    this.urlVars = parseUrlVars();
  }

  /**
   * Every business event in the app goes through here.
   *
   * `identity` is optional and only supplied by the handful of call sites that
   * know the user's email directly; it takes precedence over the SDK's persisted
   * id. Those call sites used to POST to the JSEvent lambda themselves and had a
   * separate trackLambdaEvent() method - the lambda is gone and so is the split.
   */
  track(eventName: string, properties?: any, identity?: string): void {
    const props = properties || {};
    try {
      if (!props["eg-timestamp"]) {
        props["eg-timestamp"] = new Date().toISOString();
      }
      this.rudderStackService.track(eventName, props, identity);
    } catch (e) {
      console.warn("[EventService] rudderstack track failed:", eventName, e);
    }
  }

  /**
   * Named page view. The firePageLoadEvent() global this replaced was a pure
   * pass-through to analytics.page(name, properties) - it added nothing - so no
   * property reconstruction is needed here.
   */
  page(pageName: string, pageProperties?: any): void {
    const props = pageProperties || {};
    try {
      this.rudderStackService.page(pageName, props);
    } catch (e) {
      console.warn("[EventService] rudderstack page failed:", pageName, e);
    }
  }

  /**
   * Replaces the bare window.analytics.page() on router NavigationEnd.
   * Kept separate from page() because it carries no name or properties.
   */
  pageView(): void {
    try {
      this.rudderStackService.page();
    } catch (e) {
      console.warn("[EventService] rudderstack page failed:", e);
    }
  }

  /**
   * Session registration.
   *
   * The CDN global this replaced built the ENTIRE property set itself, with the
   * app passing only the webinar name (km_event_fire.js:2081). That property set
   * is rebuilt here to match it exactly - verify the FSReg_* properties by hand
   * during QA, since there is no longer a second stream to compare against.
   * One call site (marketing-message.component.ts:163), low volume.
   */
  trackSessionRegistration(eventName: string, webinarName: string): void {
    try {
      const props: any = {};
      this.addDeviceProperties(props, eventName);
      this.addKeywordProperties(props, eventName);
      props["FSReg_Channel"] = "free_trialregular";
      props["FSReg_ReasonDetail"] = webinarName;
      props["eg-timestamp"] = new Date().toISOString();
      this.rudderStackService.track(eventName, props);
    } catch (e) {
      console.warn("[EventService] session registration failed:", e);
    }
  }

  /** Clear RudderStack identity on logout. Mirrors CustomerIoService.reset(). */
  reset(): void {
    this.rudderStackService.reset();
  }

  /** Re-run identify, e.g. after login when userLoginCookie has been written. */
  identify(identityOverride?: string): void {
    this.rudderStackService.identify(identityOverride);
  }

  // ---------------------------------------------------------------------------
  // Property builders — ports of get_device / add_keyword_properties
  // ---------------------------------------------------------------------------

  /**
   * Port of get_device (km_event_fire.js:1715). Note the property names: the OS key
   * contains a SPACE ("FSReg_Operating system"). Marketing reports depend on these
   * strings exactly as written — do not normalise them.
   */
  private addDeviceProperties(properties: any, eventName: string): any {
    const prefix = DEVICE_MAP[eventName];
    if (typeof prefix === "undefined") {
      return properties;
    }
    const ua = getUserAgent();
    properties[prefix + "_Device"] = detectDeviceType(ua);
    properties[prefix + "_Operating system"] = detectOperatingSystem(ua);
    return properties;
  }

  /**
   * Port of add_keyword_properties (km_event_fire.js:1192). The CDN reads the raw,
   * still-encoded query value and runs decodeURI over it; replicated here.
   */
  private addKeywordProperties(properties: any, eventName: string): any {
    const prefix = KEYWORD_MAP[eventName];
    if (typeof prefix === "undefined") {
      return properties;
    }
    const keyword = this.urlVars["keyword"];
    if (typeof keyword !== "undefined" && keyword !== "") {
      try {
        properties[prefix + "_keyword"] = decodeURI(keyword);
      } catch (e) {
        properties[prefix + "_keyword"] = keyword;
      }
    }
    return properties;
  }
}

/**
 * The reachable slice of devicemap (km_event_fire.js:616) and keywordmap (:635).
 *
 * The CDN maps carry 14 and 7 entries, covering events fired by the public site,
 * blogs and cart. This app can reach exactly one of them: these maps are consulted
 * ONLY by trackSessionRegistration(), whose single caller
 * (marketing-message.component.ts:163) passes "registered for free session".
 * Everything else was dead weight and is deliberately not carried over — if a new
 * event here ever needs device or keyword properties, copy that row across then.
 *
 * Kept as maps rather than an inlined "FSReg" so the lookup still MISSES for any
 * other event name, matching get_device()/add_keyword_properties(), which add
 * nothing when the event is absent from the map.
 */
const DEVICE_MAP: { [eventName: string]: string } = {
  "registered for free session": "FSReg",
};

const KEYWORD_MAP: { [eventName: string]: string } = {
  "registered for free session": "FSReg",
};

/**
 * Port of getUrlVars (km_event_fire.js:724), including its quirk of not decoding
 * values (add_keyword_properties applies decodeURI itself).
 */
function parseUrlVars(): { [key: string]: string } {
  const vars: { [key: string]: string } = {};
  try {
    const href = window.location.href;
    const query = href.slice(href.indexOf("?") + 1);
    const pairs = query.split("&");
    for (let i = 0; i < pairs.length; i++) {
      const pair = pairs[i].split("=");
      if (pair[0] && !(pair[0] in vars)) {
        vars[pair[0]] = pair[1];
      }
    }
  } catch (e) {
    /* no query params is a perfectly normal state */
  }
  return vars;
}
